import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import {
  CreditCard, Search, AlertCircle, CheckCircle, Loader2,
} from "lucide-react";

/* ── tab constants ── */
const TAB = { MAPPING: "MAPPING", HISTORY: "HISTORY" };

const FAULT_LABELS = {
  0: "Account credited",
  1: "Duplicate UTR",
  2: "Card not assigned",
  3: "Date & time Error",
  4: "Invalid Checksum",
  5: "Invalid Amount",
  6: "Card Blocked / not assigned",
  7: "Invalid Corp code",
};

const getToday = () => new Date().toISOString().split("T")[0];

/* ── reusable input ── */
const Inp = ({ label, value, onChange, placeholder, maxLength, type = "text", disabled }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      type={type} value={value} onChange={onChange}
      placeholder={placeholder} maxLength={maxLength} disabled={disabled}
      className="w-full h-10 border border-slate-300 rounded-lg px-3 text-sm
                 focus:border-[#0A2A4F] focus:outline-none bg-gray-50 disabled:opacity-60"
    />
  </div>
);

/* ── status pill ── */
const StatusPill = ({ value }) => {
  const ok = value === "SUCCESS";
  const fail = value === "FAILED";
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
      ok   ? "bg-emerald-100 text-emerald-700" :
      fail ? "bg-rose-100 text-rose-700" :
             "bg-amber-100 text-amber-700"
    }`}>
      {value || "—"}
    </span>
  );
};

/* ── active pill ── */
const ActivePill = ({ active }) => (
  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
    active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
  }`}>
    {active ? "Active" : "Inactive"}
  </span>
);

export default function AxisCard() {
  const mid = useSelector((state) => state.auth.employeeDetails?.username);

  /* ── API hooks ── */
  const { callApi: createMappingApi, loading: mappingLoading } = useApi("/admin/axis/cdm/card-mapping",  "POST");
  const { callApi: getMappingsApi,   loading: mappingsLoading } = useApi("/admin/axis/cdm/card-mapping", "GET");
  const { callApi: getHistoryApi,    loading: historyLoading }  = useApi("/admin/axis/cdm/history",      "GET");

  // const { callApi: deactivateApi,    loading: deactivateLoading } = useApi("/admin/axis/cdm/card-mapping/deactivate", "POST");
  // const { callApi: allocateStockApi, loading: stockLoading }      = useApi("/admin/axis/cdm/stock-allocation",        "POST");
  // const { callApi: getStockApi,      loading: stockSumLoading }   = useApi("/admin/axis/cdm/stock-allocation",        "GET");

  /* ── tab ── */
  const [tab, setTab] = useState(TAB.MAPPING);

  /* ── feedback ── */
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");
  const clr = () => { setError(""); setSuccess(""); };

  /* ═══════ CARD MAPPING state ═══════ */
  const [mapForm, setMapForm] = useState({ cdmCardNo: "", agentEmbossCode: "" });
  const mapF = (f) => (e) => setMapForm((p) => ({ ...p, [f]: e.target.value }));

  const [mappings,      setMappings]      = useState([]);
  const [confirmPopup, setConfirmPopup] = useState(false);

  /* ═══════ HISTORY state ═══════ */
  const [history,  setHistory]  = useState([]);
  const [histFrom, setHistFrom] = useState(getToday());
  const [histTo,   setHistTo]   = useState(getToday());
  const [histPage, setHistPage] = useState(1);
  const HIST_PAGE_SIZE = 20;

  /* ═══════ STOCK state (commented out) ═══════ */
  // const [stockForm,    setStockForm]    = useState({ distributorMid: "", quantity: "", remarks: "" });
  // const stockF = (f) => (e) => setStockForm((p) => ({ ...p, [f]: e.target.value }));
  // const [stockSummary, setStockSummary] = useState([]);

  /* ── load mappings on mount ── */
  useEffect(() => {
    fetchMappings();
    // fetchStock();
    // eslint-disable-next-line
  }, []);

  /* ═══════ HANDLERS ═══════ */

  const fetchMappings = async (mId) => {
    clr();
    try {
      const effectiveMid = mId || mid;
      const q = effectiveMid ? `mid=${effectiveMid}` : "";
      const res = await getMappingsApi(null, q || undefined);
      setMappings(Array.isArray(res) ? res : []);
    } catch {
      setMappings([]);
    }
  };

  const handleSaveMappingClick = () => {
    clr();
    if (!mapForm.cdmCardNo) {
      return setError("Card number is required.");
    }
    setConfirmPopup(true);
  };

  const handleCreateMapping = async () => {
    setConfirmPopup(false);
    try {
      const res = await createMappingApi({
        cdmCardNo:       mapForm.cdmCardNo.trim(),
        agentEmbossCode: mapForm.agentEmbossCode.trim(),
        mid,
      });
      if (res?.active === true) {
        setSuccess("Card assigned successfully.");
        setMapForm({ cdmCardNo: "", agentEmbossCode: "" });
        fetchMappings();
      } else {
        setError(res?.message || res?.error || "Mapping failed.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Mapping failed.");
    }
  };

  // const handleDeactivate = async () => {
  //   clr();
  //   if (!deactivateCard.trim()) return setError("Enter a card number to deactivate.");
  //   try {
  //     await deactivateApi(null, `cdmCardNo=${deactivateCard.trim()}`);
  //     setSuccess(`Card ${deactivateCard} deactivated.`);
  //     setDeactivateCard("");
  //     fetchMappings();
  //   } catch (e) {
  //     setError(e?.response?.data?.message || e?.message || "Deactivation failed.");
  //   }
  // };

  const fetchHistory = async () => {
    clr();
    setHistPage(1);
    try {
      let q = `mid=${mid}&`;
      if (histFrom) q += `fromDate=${histFrom}&`;
      if (histTo)   q += `toDate=${histTo}`;
      const res = await getHistoryApi(null, q);
      setHistory(Array.isArray(res) ? res : []);
    } catch {
      setHistory([]);
    }
  };

  /* ── auto-fetch history when switching to History tab ── */
  useEffect(() => {
    if (tab === TAB.HISTORY) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // const fetchStock = async () => {
  //   try {
  //     const res = await getStockApi();
  //     setStockSummary(Array.isArray(res) ? res : []);
  //   } catch {
  //     setStockSummary([]);
  //   }
  // };

  // const handleAllocateStock = async () => {
  //   clr();
  //   if (!stockForm.distributorMid || !stockForm.quantity) {
  //     return setError("Distributor MID and quantity are required.");
  //   }
  //   if (Number(stockForm.quantity) < 1) {
  //     return setError("Quantity must be at least 1.");
  //   }
  //   try {
  //     const res = await allocateStockApi({
  //       distributorMid: stockForm.distributorMid.trim(),
  //       quantity:        Number(stockForm.quantity),
  //       remarks:         stockForm.remarks.trim(),
  //     });
  //     setSuccess(`Allocated ${res?.quantity || stockForm.quantity} cards to ${stockForm.distributorMid}.`);
  //     setStockForm({ distributorMid: "", quantity: "", remarks: "" });
  //     fetchStock();
  //   } catch (e) {
  //     setError(e?.response?.data?.message || e?.message || "Allocation failed.");
  //   }
  // };

  /* ── history pagination + search ── */
  const totalHistPages = Math.ceil(history.length / HIST_PAGE_SIZE);
  const pagedHistory   = history.slice((histPage - 1) * HIST_PAGE_SIZE, histPage * HIST_PAGE_SIZE);

  /* ═══════ TABS HEADER ═══════ */
  const tabs = [
    { key: TAB.MAPPING, label: "Card Mapping",        icon: <CreditCard size={16} /> },
    { key: TAB.HISTORY, label: "Transaction History", icon: <Search size={16} /> },
    // { key: TAB.STOCK,   label: "Stock Allocation",    icon: <Package size={16} /> },
  ];

  return (
    <div className="min-h-screen p-6 bg-[#EEF1F5]">

      {/* ── Page title ── */}
      <div className="mb-6 flex items-center gap-3">
        <div className="p-2.5 bg-[#0A2A4F] rounded-xl">
          <CreditCard className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0A2A4F]"> CDM Card</h1>
          <p className="text-sm text-slate-500 mt-0.5">Card mapping &amp; transaction history</p>
        </div>
      </div>

      {/* ── Feedback banners ── */}
      {error && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-sm">
          <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="flex gap-2 mb-6 bg-white rounded-2xl p-1.5 shadow-sm border border-slate-100 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); clr(); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              tab === t.key
                ? "bg-[#0A2A4F] text-white shadow-md"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════ TAB: CARD MAPPING ═══════════════════ */}
      {tab === TAB.MAPPING && (
        <div className="space-y-6">

          {/* Map / update a card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h2 className="text-lg font-semibold text-[#0A2A4F] mb-4">Map Card to Merchant</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Inp label="CDM Card Number *" value={mapForm.cdmCardNo}
                onChange={mapF("cdmCardNo")} placeholder="4000123456789012" />
              <Inp label="Agent Emboss Code" value={mapForm.agentEmbossCode}
                onChange={(e) => setMapForm((p) => ({ ...p, agentEmbossCode: e.target.value.toUpperCase() }))} placeholder="AGT001" />
            </div>

            <button
              onClick={handleSaveMappingClick}
              disabled={mappingLoading}
              className="flex items-center gap-2 px-6 h-10 rounded-lg bg-[#0A2A4F] text-white text-sm
                         font-semibold hover:bg-[#1E4D8C] transition disabled:opacity-60"
            >
              {mappingLoading ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
              {mappingLoading ? "Saving…" : "Save Mapping"}
            </button>
          </div>

          {/* View existing mappings */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h2 className="text-lg font-semibold text-[#0A2A4F] mb-4">Existing Mappings</h2>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-[#0A2A4F] text-white">
                  <tr>
                    {["Card Number", "Agent Code", "Status", "Created At"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {mappingsLoading ? (
                    <tr><td colSpan={4} className="py-10 text-center text-slate-400">
                      <Loader2 className="animate-spin mx-auto" size={24} />
                    </td></tr>
                  ) : mappings.length === 0 ? (
                    <tr><td colSpan={4} className="py-10 text-center text-slate-400">No mappings found</td></tr>
                  ) : mappings.map((m, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-medium">{m.cdmCardNo}</td>
                      <td className="px-4 py-3">{m.agentEmbossCode || "—"}</td>
                      <td className="px-4 py-3"><ActivePill active={m.active} /></td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {m.createdAt
                          ? new Date(m.createdAt).toLocaleString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit", hour12: true,
                            })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════ TAB: HISTORY ═══════════════════ */}
      {tab === TAB.HISTORY && (
        <div className="space-y-6">

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex flex-wrap items-end gap-4">

              <div>
                <label className="block text-xs text-slate-500 mb-1">From Date</label>
                <input type="date" value={histFrom} onChange={(e) => setHistFrom(e.target.value)}
                  className="h-10 px-3 border rounded-lg bg-gray-50 text-sm" />
              </div>

              <div>
                <label className="block text-xs text-slate-500 mb-1">To Date</label>
                <input type="date" value={histTo} min={histFrom} onChange={(e) => setHistTo(e.target.value)}
                  className="h-10 px-3 border rounded-lg bg-gray-50 text-sm" />
              </div>

              <button
                onClick={fetchHistory}
                disabled={historyLoading}
                className="flex items-center gap-2 h-10 px-6 rounded-lg bg-[#0A2A4F] text-white text-sm
                           font-semibold hover:bg-[#1E4D8C] transition disabled:opacity-60"
              >
                {historyLoading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                Apply
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0A2A4F] text-white">
                  <tr>
                    {["UTR", "Card No", "Agent Code", "Amount", "Date & Time", "Status", "Message"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {historyLoading ? (
                    <tr><td colSpan={7} className="py-12 text-center text-slate-400">
                      <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                      Loading…
                    </td></tr>
                  ) : pagedHistory.length === 0 ? (
                    <tr><td colSpan={7} className="py-12 text-center text-slate-400">
                      No transaction records found
                    </td></tr>
                  ) : pagedHistory.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs font-medium text-slate-700">{r.utr || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.cdmCardNo || "—"}</td>
                      <td className="px-4 py-3">{r.agentEmbossCode || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-slate-800">
                        ₹{Number(r.txnAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{r.reqDtTime || "—"}</td>
                      <td className="px-4 py-3"><StatusPill value={r.txnStatus} /></td>
                      <td className="px-4 py-3 text-xs text-slate-600">{r.responseMessage || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalHistPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between text-sm text-slate-600">
                <span>Page <b>{histPage}</b> of <b>{totalHistPages}</b> &nbsp;·&nbsp; {history.length} records</span>
                <div className="flex gap-2">
                  <button disabled={histPage === 1} onClick={() => setHistPage((p) => p - 1)}
                    className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 font-medium">
                    ← Prev
                  </button>
                  <button disabled={histPage === totalHistPages} onClick={() => setHistPage((p) => p + 1)}
                    className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 font-medium">
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════ TAB: STOCK ALLOCATION (commented out) ═══════════════════ */}
      {/*
      {tab === TAB.STOCK && (
        <div className="space-y-6">
          ...stock allocation UI...
        </div>
      )}
      */}

      {/* ── Confirmation popup ── */}
      {confirmPopup && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-base font-bold text-slate-800">Confirm Card Mapping</h3>
            </div>

            <p className="text-sm text-slate-700 mb-1">
              For the first-time card mapping, <span className="font-bold text-red-600">₹236 (₹200 + 18% GST)</span> will be deducted from your account.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              पहली बार कार्ड मैपिंग पर <span className="font-bold text-red-600">₹236 (₹200 + 18% GST)</span> आपके खाते से काटे जाएंगे।
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmPopup(false)}
                className="flex-1 h-10 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMapping}
                disabled={mappingLoading}
                className="flex-1 h-10 rounded-lg bg-[#0A2A4F] text-white text-sm font-semibold hover:bg-[#1E4D8C] transition disabled:opacity-60"
              >
                {mappingLoading ? <Loader2 size={15} className="animate-spin mx-auto" /> : "OK, Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
